import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CompanyListItem } from '../types';
import { sysadminService } from '../services/sysadmin.service';

type SysadminMode = 'system' | 'company';

interface SysadminState {
  companies: CompanyListItem[];
  selectedCompanyId: string | null;
  selectedCompany: CompanyListItem | null;
  isLoadingCompanies: boolean;
  mode: SysadminMode;

  // Actions
  fetchCompanies: () => Promise<void>;
  selectCompany: (id: string | null) => void;
  reset: () => void;
}

export const useSysadminStore = create<SysadminState>()(
  persist(
    (set, get) => ({
      companies: [],
      selectedCompanyId: null,
      selectedCompany: null,
      isLoadingCompanies: false,
      mode: 'system',

      fetchCompanies: async () => {
        set({ isLoadingCompanies: true });
        try {
          const companies = await sysadminService.getCompanies();
          set({ companies, isLoadingCompanies: false });
        } catch (error) {
          console.error('Failed to fetch companies:', error);
          set({ isLoadingCompanies: false });
        }
      },

      selectCompany: (id: string | null) => {
        if (id === null) {
          set({
            selectedCompanyId: null,
            selectedCompany: null,
            mode: 'system',
          });
        } else {
          const { companies } = get();
          const company = companies.find(c => c.id === id) || null;
          set({
            selectedCompanyId: id,
            selectedCompany: company,
            mode: 'company',
          });
        }
      },

      reset: () => {
        set({
          companies: [],
          selectedCompanyId: null,
          selectedCompany: null,
          isLoadingCompanies: false,
          mode: 'system',
        });
      },
    }),
    {
      name: 'sysadmin-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedCompanyId: state.selectedCompanyId,
        selectedCompany: state.selectedCompany,
        mode: state.mode,
      }),
    }
  )
);
